import axios from "axios";

type UnauthorizedHandler = () => void;
type TokenRefresher = () => Promise<void>;

let unauthorizedHandlerRef: UnauthorizedHandler | null = null;
let tokenRefresherRef: TokenRefresher | null = null;
// De-dupes concurrent refreshes so many 401s share one token call.
let refreshPromise: Promise<void> | null = null;
// Latch so that concurrent 401s only trigger a single logout/redirect.
let isHandlingUnauthorized = false;

// Called once from an Auth0-aware component (see AuthLayout) to expose the
// logout/redirect behaviour to the plain-function API client.
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
    unauthorizedHandlerRef = handler;
}

export function handleUnauthorized(): void {
    if (isHandlingUnauthorized) return;
    isHandlingUnauthorized = true;
    unauthorizedHandlerRef?.();
}

// Called once from an Auth0-aware component (see AuthLayout) to expose the
// silent token-refresh behaviour to the plain-function API client. The
// refresher is responsible for fetching a fresh token AND publishing it via
// tokenInterceptor so retried requests use the new bearer.
export function setTokenRefresher(refresher: TokenRefresher): void {
    tokenRefresherRef = refresher;
}

// Single-flight silent refresh: the first caller starts the refresh, everyone
// else awaits the same promise. Rejects if no refresher has been registered.
export function refreshToken(): Promise<void> {
    if (!tokenRefresherRef) {
        return Promise.reject(new Error("No token refresher registered"));
    }
    if (!refreshPromise) {
        refreshPromise = tokenRefresherRef().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
}

export default function tokenInterceptor(token: string): void {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
}